import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./plansBilling.css";
import { SoftCard, PrimaryButton, SecondaryButton } from "./ui";
import { useAuth } from "../../contexts/AuthContext";
import { getRegion } from "../../utils/regions";
import { getUserPlan } from "../../utils/planAccess";
import {
  formatDay,
  parseDate,
  resolveSubState,
  useRegionSubs,
} from "./planState";

const DEFAULT_REGION = "healjai";

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <span className="text-astria-muted">{label}</span>
    <span className="text-right text-astria-text">{children}</span>
  </div>
);

/** Account → Profile: the account's current plan at a glance. */
const PlanSummary: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subs, loaded } = useRegionSubs();

  // Prefer the account's active region; otherwise show the plan that ends last.
  const homeRegion =
    getRegion(user?.region || DEFAULT_REGION)?.value || DEFAULT_REGION;
  let regionValue = homeRegion;
  if (!resolveSubState(subs, homeRegion, user).sub && subs.length > 0) {
    const latest = [...subs].sort(
      (a, b) =>
        (parseDate(b.endDate)?.getTime() ?? 0) -
        (parseDate(a.endDate)?.getTime() ?? 0),
    )[0];
    regionValue = getRegion(latest.region)?.value || homeRegion;
  }

  const { sub, owned, expired, cancelled, daysLeft } = resolveSubState(
    subs,
    regionValue,
    user,
  );
  const region = getRegion(regionValue);
  const regionName = t(
    `astriaHome.regionName.${regionValue}`,
    region?.label || regionValue,
  ) as string;
  const goPlans = () =>
    navigate(
      `/account/plans-billing?region=${encodeURIComponent(regionValue)}`,
    );

  return (
    <SoftCard>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-light text-astria-text">
          {t("astria.planYours", "Your plan")}
        </h3>
        {sub && (
          <span
            className={`pb-pill ${expired ? "pb-pill--expired" : "pb-pill--active"}`}
          >
            {expired
              ? t("astria.planStateExpired", "Expired")
              : cancelled
                ? t("astria.planStateCancelled", "Cancelled")
                : t("astria.planStateActive", "Active")}
          </span>
        )}
      </div>

      <div className="mt-2 divide-y divide-white/5">
        <Row label={t("astria.planSubPlan", "Plan")}>
          {getUserPlan(user) === "global"
            ? t("astria.planGlobal", "Global")
            : sub
            ? `${t("astria.planPro")} · ${
                sub.planName ||
                (sub.cadence === "annual"
                  ? t("astria.planYearly", "Yearly")
                  : t("astria.planMonthly", "Monthly"))
              }`
            : loaded
              ? t("astria.planFree", "Free")
              : "…"}
        </Row>
        <Row label={t("astria.planRegion", "Region")}>{regionName}</Row>
        {sub && (
          <>
            <Row label={t("astria.planSubStarted", "Started")}>
              {formatDay(sub.startDate)}
            </Row>
            <Row
              label={
                expired
                  ? t("astria.planSubExpiredOn", "Expired on")
                  : cancelled
                    ? t("astria.planSubEndsOn", "Ends on")
                    : t("astria.planSubRenewsOn", "Renews on")
              }
            >
              {formatDay(sub.endDate)}
            </Row>
            {owned && daysLeft !== null && (
              <Row label={t("astria.planSubLeft", "Time left")}>
                {t("astria.planSubDays", {
                  count: daysLeft,
                  defaultValue: "{{count}} days",
                })}
              </Row>
            )}
          </>
        )}
      </div>

      {owned ? (
        <SecondaryButton className="mt-4 w-full" onClick={goPlans}>
          {t("astria.planManage", "Manage plan")}
        </SecondaryButton>
      ) : (
        <PrimaryButton className="mt-4 w-full" onClick={goPlans}>
          {expired
            ? t("astria.planRenew", "Renew Pro")
            : t("astria.planSee", "See plans")}
        </PrimaryButton>
      )}
    </SoftCard>
  );
};

export default PlanSummary;
